

import { useState, useEffect, useCallback } from 'react';
import type { ImportedBill } from '../types';
import { 
    getImportedBills, 
    addImportedBill as addDB, 
    updateImportedBill as updateDB, 
    deleteImportedBillDB,
    mergeImportedBillsDB
} from '../services/db';
import { postMessage, useBroadcastListener } from '../services/broadcastService';

const sortImportedBills = (bills: ImportedBill[]) => bills.sort((a, b) => new Date(b.sharedData.bill.date).getTime() - new Date(a.sharedData.bill.date).getTime());

export const useImportedBills = () => {
  const [importedBills, setImportedBills] = useState<ImportedBill[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadImportedBills = useCallback(async (isInitialLoad: boolean = false) => {
    if (isInitialLoad) setIsLoading(true);
    try {
      let dbBills = await getImportedBills();
      
      const billsToUpdate: ImportedBill[] = [];
      const billsAfterMigration = dbBills.map(bill => {
          if (bill.id.startsWith('summary-') && !bill.summaryRecalculated) {
              console.log(`Performing one-time recalculation for summary bill: ${bill.id}`);
              const migratedBill: ImportedBill = JSON.parse(JSON.stringify(bill)); 

              const myNameInSummary = migratedBill.sharedData.bill.participants.find(p => p.id === migratedBill.myParticipantId)?.name;
              
              if (myNameInSummary) {
                  let totalOwedByMe = 0;
                  let totalPortion = 0;
                  let allConstituentsPaid = true;
                  const paidItems = { ...(migratedBill.localStatus.paidItems || {}) };
                  
                  if (migratedBill.sharedData.bill.items) {
                      for (const item of migratedBill.sharedData.bill.items) {
                          totalPortion += item.price;
                          
                          let isConsideredPaid = false;
                          const originalBill = item.originalBillData;
                          if (originalBill) {
                              const myParticipantInOriginal = originalBill.participants.find(p => p.name.toLowerCase().trim() === myNameInSummary.toLowerCase().trim());
                              if (myParticipantInOriginal?.paid) {
                                  paidItems[item.id] = true;
                              }
                          }
                          
                          if (paidItems[item.id]) {
                              isConsideredPaid = true;
                          }

                          if (!isConsideredPaid) {
                              totalOwedByMe += item.price;
                              allConstituentsPaid = false;
                          }
                      }
                  }

                  migratedBill.sharedData.bill.totalAmount = totalPortion;
                  if (migratedBill.sharedData.bill.participants[0]) {
                      migratedBill.sharedData.bill.participants[0].amountOwed = totalOwedByMe;
                  }
                  migratedBill.localStatus.paidItems = paidItems;
                  migratedBill.localStatus.myPortionPaid = allConstituentsPaid;
                  migratedBill.summaryRecalculated = true;

                  billsToUpdate.push(migratedBill);
                  return migratedBill;
              }
          }
          return bill;
      });

      if (billsToUpdate.length > 0) {
          console.log(`Saving ${billsToUpdate.length} migrated summary bills.`);
          await mergeImportedBillsDB([], billsToUpdate);
      }
      
      setImportedBills(sortImportedBills(billsAfterMigration));
    } catch (err) {
      console.error("Failed to load imported bills:", err);
    } finally {
      if (isInitialLoad) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadImportedBills(true);
  }, [loadImportedBills]);

  useBroadcastListener(useCallback(message => {
    if (message.type === 'imported-bills-updated') {
      loadImportedBills(false);
    }
  }, [loadImportedBills]));

  const addImportedBill = useCallback(async (newBill: ImportedBill) => {
    await addDB(newBill);
    setImportedBills(prev => sortImportedBills([newBill, ...prev]));
    postMessage({ type: 'imported-bills-updated' });
  }, []);

  const updateImportedBill = useCallback(async (updatedBill: ImportedBill) => {
    await updateDB(updatedBill);
    setImportedBills(prev => sortImportedBills(prev.map(b => b.id === updatedBill.id ? updatedBill : b)));
    postMessage({ type: 'imported-bills-updated' });
  }, []);

  const updateMultipleImportedBills = useCallback(async (billsToUpdate: ImportedBill[]) => {
      if (billsToUpdate.length === 0) return;
      
      // The `billsToUpdate` array coming from the polling service now has pre-calculated statuses
      // for both regular and summary bills. We can trust it directly.
      await mergeImportedBillsDB([], billsToUpdate);
      const updatedMap = new Map(billsToUpdate.map(b => [b.id, b]));
      setImportedBills(prev => sortImportedBills(prev.map(b => updatedMap.get(b.id) || b)));
      postMessage({ type: 'imported-bills-updated' });
  }, []);

  const deleteImportedBill = useCallback(async (billId: string) => {
    await deleteImportedBillDB(billId);
    setImportedBills(prev => prev.filter(b => b.id !== billId));
    postMessage({ type: 'imported-bills-updated' });
  }, []);

  const archiveImportedBill = useCallback(async (billId: string) => {
    const billToUpdate = importedBills.find(b => b.id === billId);
    if (billToUpdate) {
      const updatedBill = { ...billToUpdate, status: 'archived' as const };
      await updateImportedBill(updatedBill);
    }
  }, [importedBills, updateImportedBill]);

  const unarchiveImportedBill = useCallback(async (billId: string) => {
    const billToUpdate = importedBills.find(b => b.id === billId);
    if (billToUpdate) {
      const updatedBill = { ...billToUpdate, status: 'active' as const };
      await updateImportedBill(updatedBill);
    }
  }, [importedBills, updateImportedBill]);

  const mergeImportedBills = useCallback(async (billsToMerge: (Omit<ImportedBill, 'status' | 'liveStatus'>)[]) => {
      // FIX: Explicitly type Map to resolve type inference issues.
      const existingBillMap = new Map<string, ImportedBill>(importedBills.map(b => [b.id, b]));
      const billsToAdd: ImportedBill[] = [];
      const billsToUpdate: ImportedBill[] = [];
      let skippedCount = 0;

      // FIX: Replaced forEach with for...of loop to resolve TypeScript inference issues.
      for (const billToProcess of billsToMerge) {
          const existingBill = existingBillMap.get(billToProcess.id);
          if (existingBill) {
              if ((billToProcess.lastUpdatedAt ?? 0) > (existingBill.lastUpdatedAt ?? 0)) {
                  billsToUpdate.push({ ...existingBill, ...billToProcess, status: existingBill.status, liveStatus: 'live' });
              } else {
                  skippedCount++;
              }
          } else {
              billsToAdd.push({ ...billToProcess, status: 'active', liveStatus: 'live' });
          }
      }

      if (billsToAdd.length > 0 || billsToUpdate.length > 0) {
          await mergeImportedBillsDB(billsToAdd, billsToUpdate);
          setImportedBills(prev => {
              // FIX: Explicitly type Map to resolve type inference issues.
              const prevMap = new Map<string, ImportedBill>(prev.map(b => [b.id, b]));
              billsToUpdate.forEach(b => prevMap.set(b.id, b));
              billsToAdd.forEach(b => prevMap.set(b.id, b));
              return sortImportedBills(Array.from(prevMap.values()));
          });
          postMessage({ type: 'imported-bills-updated' });
      }

      return { added: billsToAdd.length, updated: billsToUpdate.length, skipped: skippedCount };
  }, [importedBills]);
  
  return { importedBills, isLoading, addImportedBill, updateImportedBill, deleteImportedBill, archiveImportedBill, unarchiveImportedBill, mergeImportedBills, updateMultipleImportedBills };
};